export interface SubscriptionLike {
  /**
   * The backend's access verdict from /subscriptions/current — the SAME answer
   * the API gate gives guarded routes. When present it is authoritative; the
   * status/date interpretation below is only a fallback for objects that do
   * not carry it (admin listings, older payloads).
   */
  has_access?: boolean | null;
  access_state?: string | null;
  status?: string | null;
  trial_end?: string | null;
  trial_start?: string | null;
  current_period_end?: string | null;
  cancel_at_period_end?: boolean;
  [key: string]: any;
}

export function isTrialActuallyExpired(sub: SubscriptionLike | null | undefined): boolean {
  if (!sub) return false;
  // The server still grants access (its trial boundary is the END of the
  // trial_end day, UTC) — the client must not show the trial as lapsed.
  if (sub.has_access === true) return false;
  return (
    sub.status === 'trialing' &&
    !!sub.trial_end &&
    new Date(sub.trial_end) < new Date()
  );
}

export function isSubscriptionActive(sub: SubscriptionLike | null | undefined): boolean {
  if (!sub) return false;
  // Do not re-derive what the server already decided — a second
  // interpretation of status strings and dates is exactly how the client came
  // to disagree with the gate (trial boundary, pending, stale 'active' rows).
  if (typeof sub.has_access === 'boolean') return sub.has_access;
  if (sub.status === 'active') return true;
  if (sub.status === 'trialing' && !isTrialActuallyExpired(sub)) return true;
  /*
   * 🔴 'pending' is an UNPAID checkout, not access.
   *
   * It used to return true here, which let an abandoned checkout suppress the
   * expiry gate and the renewal banner on the client even after the backend
   * stopped treating it as active. The backend is authoritative
   * (subscriptionStatus.js returns 'pending' for these rows); this keeps the two
   * in agreement instead of having the UI grant what the API denies.
   */
  if (sub.status === 'pending') return false;
  return false;
}

export function isSubscriptionExpiredOrCancelled(sub: SubscriptionLike | null | undefined): boolean {
  if (!sub) return false;
  if (sub.status === 'expired' || sub.status === 'cancelled') return true;
  if (isTrialActuallyExpired(sub)) return true;
  return false;
}
